import { useState } from "react"
import { Alert, App as AntdApp, Button, Space, Table, Tag, Tooltip } from "antd"
import type { TableColumnsType } from "antd"
import { CalendarX, CircleCheck, PauseCircle, Pencil, PlayCircle, Plus, Radio, Trash2, UserMinus, UserPlus } from "lucide-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { SRV_V2_MODULES, type TenantSummary } from "@csbi/contracts"
import { formatTimestamp, tenantColor } from "@csbi/viz-core"
import { StateBadge } from "@csbi/widgets"
import { useTheme } from "../../app/providers/theme-provider.js"
import { deleteTenant, fetchMe, fetchTenants, probeTenant, purgeTenant, updateTenant } from "../../features/tiles/api.js"
import { ApiError } from "../../shared/api/request.js"
import { PageHeader } from "../../shared/components/page-header.js"
import { DeploymentForm } from "./deployment-form.js"
import { OffboardDialog } from "./offboard-dialog.js"
import { PurgeDialog } from "./purge-dialog.js"

/**
 * ⚠ **The management actions are on this page, with no separate Deployment Registry page** (the original design had one; it was merged).
 */
/**
 * ⚠ Case 3 first said **"Collecting"**, which was wrong: a present continuous reads as "collecting right now",
 * while collection is a scheduled job and almost certainly is not running at any given instant — the label was
 * asserting something we do not know. What `state === "ok"` really means is "the **most recent** collection of
 * every module succeeded", which is a **state**, not an action, and belongs to the same class of words as the
 * other three (No data / Unsupported / Collection failing). How recent "most recent" is, is answered by the Last collected column beside it, not impersonated by this one.
 */
function CollectionStatus({ tenant }: { tenant: TenantSummary }) {
  /**
   * ⚠ Churned comes before paused, because offboarding also pauses — so a former customer would otherwise read
   * as merely "Paused", and the two mean different things to whoever is looking. Paused says "we will come
   * back to this"; offboarded says "they are gone, and that is why they are missing from the comparisons".
   */
  if (tenant.churned)
    return (
      <Tooltip
        title={`Left on ${tenant.offboardedAt ?? "an unrecorded date"}. History is kept and still queryable, but they are out of the default cross-customer statistics.`}
      >
        <Tag icon={<UserMinus size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />} style={{ marginInlineEnd: 0 }}>
          Offboarded
        </Tag>
      </Tooltip>
    )

  if (!tenant.active)
    return (
      <Tooltip title="Collection is switched off for this deployment. Nothing on this row will change until it is switched back on.">
        <Tag icon={<PauseCircle size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />} style={{ marginInlineEnd: 0 }}>
          Paused
        </Tag>
      </Tooltip>
    )

  /**
   * ⚠ **Overdue outranks the four states, because when it is true the four states are out of date.**
   */
  if (tenant.collectionOverdue)
    return (
      <Tooltip
        title={
          tenant.nextRunAt
            ? `Collector did not run. Due ${new Date(tenant.nextRunAt).toLocaleString()} — our side, not the customer's.`
            : "Never scheduled — the collector has not picked this up."
        }
      >
        <Tag
          color="warning"
          icon={<CalendarX size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />}
          style={{ marginInlineEnd: 0 }}
        >
          Overdue
        </Tag>
      </Tooltip>
    )

  const badge = (
    <StateBadge
      state={tenant.state}
      perTenant={[
        {
          tenantId: tenant.id,
          displayName: tenant.displayName,
          environment: tenant.environment,
          colorSlot: tenant.colorSlot,
          churned: tenant.churned,
          state: tenant.state,
          lastCollectedAt: tenant.lastCollectedAt,
        },
      ]}
    />
  )
  if (tenant.state !== "ok") return badge

  return (
    <Tooltip title="Every module's most recent collection succeeded.">
      <Tag
        color="success"
        icon={<CircleCheck size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />}
        style={{ marginInlineEnd: 0 }}
      >
        Healthy
      </Tag>
    </Tooltip>
  )
}

export function TenantsPage() {
  const { mode } = useTheme()
  const { message, modal } = AntdApp.useApp()
  const qc = useQueryClient()
  const [editing, setEditing] = useState<TenantSummary | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [offboarding, setOffboarding] = useState<TenantSummary | null>(null)
  /** The customer whose Purge dialog is open. Separate from `offboarding` — the two must never be confusable */
  const [purging, setPurging] = useState<TenantSummary | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ["tenants"],
    queryFn: ({ signal }) => fetchTenants(signal),
    staleTime: 5 * 60_000,
  })

  /**
   * ⚠ Without elevation the buttons are **not rendered**, rather than rendered disabled, and certainly not left to hit a 403.
   */
  const { data: staff } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: ({ signal }) => fetchMe(signal),
    staleTime: 10 * 60_000,
    retry: false,
  })
  const canManage = staff?.capabilities.includes("deployments") ?? false

  const probe = useMutation({
    mutationFn: probeTenant,
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["tenants"] })
      void message.success(
        `Reachable — ${r.host ?? "unknown host"}, srv ${r.srvVersion ?? "version unknown"}, ${r.availableModules?.length ?? 0}/${SRV_V2_MODULES.length} modules.`,
        6,
      )
    },
    onError: (err) =>
      void message.error(
        (err instanceof ApiError ? err.message : "The probe failed.") +
          " Check the address and that SHARED_SECRET matches on that srv.",
        10,
      ),
  })

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => updateTenant(id, { active }),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: ["tenants"] })
      void message.success(v.active ? "Collection resumed." : "Collection paused. History is kept.")
    },
    onError: (err) => void message.error(err instanceof ApiError ? err.message : "The request failed."),
  })

  const setOffboarded = useMutation({
    mutationFn: ({ id, offboardedAt }: { id: string; offboardedAt: string | null }) =>
      updateTenant(id, { offboardedAt }),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: ["tenants"] })
      void message.success(
        v.offboardedAt
          ? "Offboarded. Collection stopped; all history is kept."
          : "Marked as a current customer again. Collection stays paused until you resume it.",
        6,
      )
    },
    onError: (err) => void message.error(err instanceof ApiError ? err.message : "The request failed."),
  })

  const remove = useMutation({
    mutationFn: deleteTenant,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tenants"] })
      void message.success("Deployment deleted.")
    },
    onError: (err) => void message.error(err instanceof ApiError ? err.message : "The request failed.", 8),
  })

  const purge = useMutation({
    mutationFn: ({ id, confirm }: { id: string; confirm: string }) => purgeTenant(id, confirm),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["tenants"] })
      void message.success(`Purged. ${r.facts.toLocaleString()} data points destroyed in ${Math.round(r.durationMs / 1000)}s.`, 8)
    },
    onError: (err) => void message.error(err instanceof ApiError ? err.message : "The purge failed.", 10),
  })

  /**
   * ⚠ This is **not** "whether the button is enabled" any more. A deployment with data is not undeletable, it
   * needs the other path — the button stays live and opens the purge dialog instead. Greying it out was right
   * only while destroying collected data was impossible.
   */
  const deletable = (t: TenantSummary) => t.metricsLinked === 0 && t.lastCollectedAt === null

  const confirmDelete = (t: TenantSummary) =>
    modal.confirm({
      title: `Delete ${t.displayName}?`,
      content:
        "It has never collected anything, so nothing is lost. This removes the registry entry — " +
        "to stop collecting from a deployment that does have data, use Pause instead.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => remove.mutateAsync(t.id),
    })

  const confirmRejoin = (t: TenantSummary) =>
    modal.confirm({
      title: `Mark ${t.displayName} as a current customer?`,
      content:
        "Their full history returns to the cross-customer statistics, in the colour they already had. " +
        "Collection stays paused — resume it separately when you are ready to call their deployment again.",
      okText: "Confirm",
      onOk: () => setOffboarded.mutateAsync({ id: t.id, offboardedAt: null }),
    })

  const confirmPause = (t: TenantSummary) =>
    modal.confirm({
      title: t.active ? `Pause collection for ${t.displayName}?` : `Resume collection for ${t.displayName}?`,
      content: t.active
        ? "Nothing on this row will change until it is switched back on. All collected history is kept — this is not a delete."
        : "It will be included in the next scheduled run.",
      okText: t.active ? "Pause" : "Resume",
      onOk: () => setActive.mutateAsync({ id: t.id, active: !t.active }),
    })

  const metricsTotal = data?.metricsTotal

  const columns: TableColumnsType<TenantSummary> = [
    {
      /**
       * ⚠ This column has to have a `width`, and the whole table has to scroll horizontally.
       */
      title: "Customer",
      dataIndex: "displayName",
      key: "displayName",
      width: 200,
      ellipsis: true,
      render: (name: string, t) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span
            aria-hidden="true"
            style={{ width: 9, height: 9, borderRadius: 999, background: tenantColor(t.colorSlot, mode) }}
          />
          <span>{name}</span>
          {t.churned && <Tag style={{ marginInlineEnd: 0 }}>Churned</Tag>}
        </span>
      ),
    },
    { title: "Environment", dataIndex: "environment", key: "environment", width: 110 },
    {
      title: "Version",
      dataIndex: "srvVersion",
      key: "srvVersion",
      width: 90,
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "Host",
      dataIndex: "detectedHost",
      key: "detectedHost",
      width: 130,
      ellipsis: true,
      render: (host: string | null, t) =>
        host ? (
          <Tooltip title={`Registry address: ${t.baseUrl}`}>{host}</Tooltip>
        ) : (
          <span style={{ color: "var(--csbi-ink-muted)" }}>not probed</span>
        ),
    },
    {
      title: "Modules",
      dataIndex: "availableModules",
      key: "availableModules",
      width: 84,
      align: "right",
      render: (mods: string[] | null) => (mods ? `${mods.length}/14` : "—"),
    },
    {
      title: "Metrics linked",
      dataIndex: "metricsLinked",
      key: "metricsLinked",
      /* 130 is the minimum at which "Metrics linked" does not wrap. Below it the header breaks into two lines */
      width: 130,
      align: "right",
      render: (n: number) => (
        <span
          style={{
            fontVariantNumeric: "tabular-nums",
            color: metricsTotal !== undefined && n === metricsTotal ? "var(--csbi-status-good-ink)" : undefined,
          }}
        >
          {n}
          {metricsTotal === undefined ? "" : `/${metricsTotal}`}
        </span>
      ),
    },
    {
      title: "Last collected",
      dataIndex: "lastCollectedAt",
      key: "lastCollectedAt",
      width: 172,
      render: (v: string | null) => (v ? formatTimestamp(v) : <span style={{ color: "var(--csbi-ink-muted)" }}>never</span>),
    },
    {
      title: "Status",
      key: "state",
      width: 112,
      render: (_: unknown, t) => <CollectionStatus tenant={t} />,
    },
  ]

  if (canManage)
    columns.push({
      title: "Actions",
      key: "actions",
      width: 164,
      align: "right",
      render: (_: unknown, t: TenantSummary) => (
        /**
         * ⚠ Icons only, with the text in a tooltip.
         */
        <Space size={2}>
          <Tooltip title="Test the connection">
            <Button
              type="text"
              size="small"
              aria-label="Probe"
              icon={<Radio size={15} />}
              loading={probe.isPending && probe.variables === t.id}
              onClick={() => probe.mutate(t.id)}
            />
          </Tooltip>
          <Tooltip title="Edit settings">
            <Button
              type="text"
              size="small"
              aria-label="Edit"
              icon={<Pencil size={15} />}
              onClick={() => { setEditing(t); setFormOpen(true) }}
            />
          </Tooltip>
          <Tooltip title={t.active ? "Pause collection" : "Resume collection"}>
            <Button
              type="text"
              size="small"
              aria-label={t.active ? "Pause" : "Resume"}
              icon={t.active ? <PauseCircle size={15} /> : <PlayCircle size={15} />}
              onClick={() => confirmPause(t)}
            />
          </Tooltip>
          <Tooltip title={t.churned ? "Mark as a current customer again" : "Customer has left"}>
            <Button
              type="text"
              size="small"
              aria-label={t.churned ? "Rejoin" : "Offboard"}
              icon={t.churned ? <UserPlus size={15} /> : <UserMinus size={15} />}
              loading={setOffboarded.isPending && setOffboarded.variables?.id === t.id}
              onClick={() => (t.churned ? confirmRejoin(t) : setOffboarding(t))}
            />
          </Tooltip>
          {/*
            ⚠ **One button, two operations, and the row's own data decides which.**
            */}
          <Tooltip title={deletable(t) ? "Delete" : "Delete this deployment and all its data"}>
            <Button
              type="text"
              size="small"
              danger
              aria-label="Delete"
              loading={(remove.isPending && remove.variables === t.id) || (purge.isPending && purge.variables?.id === t.id)}
              icon={<Trash2 size={15} />}
              onClick={() => (deletable(t) ? confirmDelete(t) : setPurging(t))}
            />
          </Tooltip>
        </Space>
      ),
    })

  const overdue = (data?.tenants ?? []).filter((t) => t.collectionOverdue)

  return (
    <>
      <PageHeader
        title="Deployments"
        extra={
          canManage ? (
            <Button
              type="primary"
              icon={<Plus size={14} />}
              onClick={() => { setEditing(null); setFormOpen(true) }}
            >
              Add deployment
            </Button>
          ) : undefined
        }
      />

      {overdue.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            overdue.length === 1
              ? `${overdue[0]!.displayName} has not been collected on schedule`
              : `${overdue.length} deployments have not been collected on schedule`
          }
          description="The collector did not run when it was due. This is on our side, not the customers' — until it runs again, every dashboard is showing the last successful collection, and the status badges describe that older collection rather than today."
        />
      )}

      <Table<TenantSummary>
        size="small"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={data?.tenants ?? []}
        pagination={false}
        scroll={{ x: 1192 }}
      />
      <DeploymentForm open={formOpen} tenant={editing} onClose={() => setFormOpen(false)} />
      <PurgeDialog
        // Remounts per customer, so the typed confirmation never carries over to a different deployment
        key={`purge-${purging?.id ?? "none"}`}
        tenant={purging}
        onCancel={() => setPurging(null)}
        onConfirm={async (confirm) => {
          await purge.mutateAsync({ id: purging!.id, confirm })
          setPurging(null)
        }}
      />
      <OffboardDialog
        key={offboarding?.id ?? "none"}
        tenant={offboarding}
        onCancel={() => setOffboarding(null)}
        onConfirm={async (offboardedAt) => {
          await setOffboarded.mutateAsync({ id: offboarding!.id, offboardedAt })
          setOffboarding(null)
        }}
      />
    </>
  )
}
